import {
    Box,
    InputAdornment,
    TextField as MuiTextField,
    TextFieldProps as MuiTextFieldProps,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    styled,
    toggleButtonGroupClasses,
} from '@mui/material';
import BudgetIcon from 'images/icons/budget.svg?react';
import React, { useState } from 'react';
import { FieldError } from 'react-hook-form';
import { EColors, getColor } from 'theme';

const StyledTextField = styled(MuiTextField, {
    shouldForwardProp: prop => prop !== 'showSymbol' && prop !== 'symbolWidth',
})<MuiTextFieldProps & { error: boolean; showSymbol?: boolean; symbolWidth?: number }>(
    ({ error, showSymbol = true, symbolWidth = 0 }) => ({
        flex: 1,
        boxSizing: 'border-box',
        width: '100%',
        height: '48px',
        fontSize: '.875rem',
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: getColor(EColors.v3Background),
            paddingLeft: showSymbol ? '16px' : '6px',
            paddingRight: '6px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            border: `1px solid ${error ? getColor(EColors.red) : getColor(EColors.v3Border)}`,
            borderRadius: '12px',
        },
        '& .MuiOutlinedInput-input': {
            borderRadius: '12px',
            padding: '12.5px',
            paddingLeft: showSymbol ? '0px' : '12px',
            fontSize: '0.875rem',
            lineHeight: '1.4375em',
        },

        // Hide increment/decrement arrows for number inputs
        '& .MuiOutlinedInput-input[type="number"]::-webkit-outer-spin-button, & .MuiOutlinedInput-input[type="number"]::-webkit-inner-spin-button':
            {
                WebkitAppearance: 'none',
                margin: 0,
            },

        '& .MuiOutlinedInput-input[type="number"]': {
            MozAppearance: 'textfield',
            // Additional styling to ensure consistent appearance
            '&::-ms-clear, &::-ms-expand': {
                display: 'none',
            },
        },

        '&:hover .MuiInputBase-root:hover > .MuiOutlinedInput-notchedOutline': {
            borderColor: getColor(EColors.v3Dark) + ' !important',
        },
        '& .MuiFormLabel-root': {
            transform: showSymbol ? `translate(${20 + symbolWidth}px, 13px)` : 'translate(14px, 13px)',
            fontSize: '0.875rem !important',
            lineHeight: '1.4375em !important',
            color: getColor(EColors.pureBlack, 0.5),
        },
        '& .MuiFormLabel-root.MuiInputLabel-shrink': {
            color: getColor(EColors.pureBlack, 0.6),
            fontSize: '0.875rem !important',
            transform: 'translate(9px, -50%) scale(1)',
            backgroundColor: getColor(EColors.pureWhite),
            padding: '0 5px',
            lineHeight: '1em',
            '& span': {
                fontSize: '0.875rem !important',
            },
        },
        '& .MuiOutlinedInput-notchedOutline legend span': {
            fontSize: '0.875rem !important',
        },
    })
);

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme: { spacing } }) => ({
    [`&.${toggleButtonGroupClasses.root}`]: {
        padding: spacing(0.5),
        backgroundColor: '#F0F0F0',
    },
    [`& .${toggleButtonGroupClasses.grouped}`]: {
        padding: spacing(0.75),
        lineHeight: 1,
        border: 'none',
        [`&.${toggleButtonGroupClasses.selected}`]: {
            borderRadius: 6,
            border: `1px ${getColor(EColors.softBorder)}`,
            backgroundColor: getColor(EColors.pureWhite),
        },
    },
}));

const StyledToggleButton = styled(ToggleButton)(({ theme: { spacing } }) => ({
    borderRadius: '4px',
    padding: spacing(1),
    fontSize: '0.75rem',
    fontWeight: 500,
    color: getColor(EColors.pureBlack, 0.5),
}));

const StyledCurrencyAdornment = styled(InputAdornment)(({ theme }) => ({
    maxWidth: '60px',
    minWidth: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
        maxWidth: '50px',
    },
    '& .MuiTypography-root': {
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
}));

type ToggleOption = { value: number | string; label: string };

type BudgetFieldProps = {
    label: string;
    name: string;
    placeholder?: string;
    error?: FieldError;
    value?: number;
    toggleValue?: string | number;
    toggleOptions?: ToggleOption[];
    disabled?: boolean;
    required?: boolean;
    showSymbol?: boolean;
    currencySymbol?: React.ReactNode;
    onChange: (value: string) => void;
    onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onChangeToggle?: (value: string) => void;
};

// Extract text content from React nodes recursively
const extractText = (node: React.ReactNode): string => {
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }
    if (Array.isArray(node)) {
        return node.map(extractText).join('');
    }
    if (React.isValidElement(node)) {
        const props = node.props as { children?: React.ReactNode };
        if (props?.children) {
            return extractText(props.children);
        }
    }
    return '';
};

// Calculate symbol width based on character count
const getSymbolWidth = (symbol: React.ReactNode): number => {
    const text = extractText(symbol);
    if (text) {
        return Math.min(text.length * 10, 60);
    }

    return 24;
};

export const BudgetField = React.forwardRef<HTMLDivElement, BudgetFieldProps>(
    (
        {
            value,
            label,
            error,
            placeholder,
            toggleValue,
            toggleOptions = [],
            onChangeToggle,
            showSymbol = false,
            currencySymbol = <BudgetIcon />,
            ...field
        },
        ref
    ) => {
        const [isFocused, setIsFocused] = useState(false);

        const inputRef = React.useRef<HTMLInputElement>(null);

        React.useImperativeHandle(ref, () => inputRef.current!);

        const symbolWidth = showSymbol ? getSymbolWidth(currencySymbol) : 0;

        // Prevent scroll wheel from changing number input values
        React.useEffect(() => {
            const input = inputRef.current;
            if (!input) return;

            const preventScroll = (e: WheelEvent) => {
                e.preventDefault();
            };

            // Use addEventListener with passive: false to allow preventDefault
            input.addEventListener('wheel', preventScroll, { passive: false });

            return () => {
                input.removeEventListener('wheel', preventScroll);
            };
        }, []);

        const onClickToggle = (_: React.MouseEvent<HTMLElement>, val: string | null) => {
            if (field.disabled) return;
            if (val) onChangeToggle?.(val);
        };

        return (
            <Box>
                <StyledTextField
                    ref={inputRef}
                    {...field}
                    label={label}
                    placeholder={placeholder}
                    error={!!error}
                    showSymbol={showSymbol}
                    symbolWidth={symbolWidth}
                    value={value}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const numericValue = e.target.value.replace(/[^\d.-]/g, '');
                        field.onChange?.(numericValue);
                    }}
                    InputProps={{
                        startAdornment: showSymbol ? (
                            <StyledCurrencyAdornment position="start">{currencySymbol}</StyledCurrencyAdornment>
                        ) : null,
                        endAdornment:
                            toggleOptions.length > 0 ? (
                                <InputAdornment position="end">
                                    <StyledToggleButtonGroup value={toggleValue} onChange={onClickToggle} exclusive>
                                        {toggleOptions.map(option => (
                                            <StyledToggleButton key={option.value} value={option.value}>
                                                {option.label}
                                            </StyledToggleButton>
                                        ))}
                                    </StyledToggleButtonGroup>
                                </InputAdornment>
                            ) : null,
                    }}
                    InputLabelProps={{
                        shrink: !!value || value === 0 || isFocused,
                    }}
                />
                {!!error && (
                    <Typography variant="body2" color={getColor(EColors.red)} fontWeight={500}>
                        {error.message}
                    </Typography>
                )}
            </Box>
        );
    }
);
